// src/pages/PostEdit.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/forum/Navbar';
import { HiPhoto, HiXMark, HiExclamationTriangle } from 'react-icons/hi2';
import { motion } from 'framer-motion';


const PostEdit = () => {
  const { id } = useParams();
  const { user, canModifyPost } = useAuth();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
    const [tags, setTags] = useState([]);
  const [currentTag, setCurrentTag] = useState('');
  const [errors, setErrors] = useState({});
    
  const popularTags = [
    'vintage', 'cursive', 'historical', 'personality', 'analysis', 'question',
    'mental-health', 'progression', 'emotional', 'professional', 'medical',
    'left-handed', 'technique', 'calligraphy', 'famous', 'research'
  ];

  useEffect(() => {
    const fetchPost = async () => {
      if (!user) {
        navigate('/login', { state: { from: `/post/edit/${id}` } });
        return;
      }

      try {
        const response = await fetch(`https://app.writeapp.in/post/${id}`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Failed to fetch post');
        }

        const postData = await response.json();

        if (!canModifyPost(postData)) {
          alert('You are not authorized to edit this post.');
          navigate('/forum', { replace: true });
          return;
        }

        setPost(postData);
        setTitle(postData.title || '');
        setContent(postData.content || '');
        setTags(postData.tags?.map(tag =>
          typeof tag === 'string' ? tag : tag.name
        ) || []);
      } catch (error) {
        console.error('Error fetching post:', error);
        alert('Error loading post for editing.');
        navigate('/forum', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPost();
  }, [id, navigate, user, canModifyPost]);

  const validateForm = () => {
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.trim().length < 5) {
      newErrors.title = 'Title must be at least 5 characters long';
    } else if (title.trim().length > 200) {
      newErrors.title = 'Title must be less than 200 characters';
    }

    if (!content.trim()) {
      newErrors.content = 'Content is required';
    } else if (content.trim().length < 10) {
      newErrors.content = 'Content must be at least 10 characters long';
    }

    if (tags.length > 10) {
      newErrors.tags = 'Maximum 10 tags allowed';
    }

    

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  

  const handleTagInputChange = (e) => {
    setCurrentTag(e.target.value);
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const addTag = () => {
    const trimmedTag = currentTag.trim().toLowerCase();
    if (trimmedTag && !tags.includes(trimmedTag) && tags.length < 10) {
      setTags([...tags, trimmedTag]);
      setCurrentTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  


const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validateForm()) return;

  setIsSubmitting(true);
  setErrors({});

  try {
    const payload = {
      title: title.trim(),
      content: content.trim(),
      tag_names: tags,
    };

    const response = await fetch(`https://app.writeapp.in/post/${post.id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('Detailed error:', data);
      throw new Error(typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail));
    }

    navigate(`/post/${post.id}`, {
      state: { success: 'Post updated successfully!' },
    });
  } catch (error) {
    console.error('Error updating post:', error);
    setErrors({ submit: error.message || 'Failed to update post. Please try again.' });
  } finally {
    setIsSubmitting(false);
  }
};




  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0B0C0D]">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#0B0C0D] text-white">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Post Not Found</h1>
            <p className="text-gray-400 mb-4">The post you're trying to edit doesn't exist.</p>
            <button
              onClick={() => navigate('/forum')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Forum
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C0D] text-white">
      <Navbar />

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-dark-gray/30 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Edit Post</h1>
                <p className="text-blue-100">Update your forum post</p>
              </div>
              <button
                onClick={() => navigate(`/post/${post.id}`)}
                className="text-white hover:text-blue-200 transition-colors"
              >
                <HiXMark className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Error Alert */}
            {errors.submit && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 flex items-center space-x-3"
              >
                <HiExclamationTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-300">{errors.submit}</p>
              </motion.div>
            )}

            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-2">
                Title *
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={`w-full px-4 py-3 bg-dark-gray/50 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-white placeholder-gray-400 ${errors.title ? 'border-red-500/50 bg-red-500/10' : 'border-gray-600'
                  }`}
                placeholder="Enter your post title..."
                maxLength={200}
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-400">{errors.title}</p>
              )}
              <p className="mt-1 text-sm text-gray-400">
                {title.length}/200 characters
              </p>
            </div>

            {/* Content */}
            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-300 mb-2">
                Content *
              </label>
              <textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={12}
                className={`w-full px-4 py-3 bg-dark-gray/50 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-vertical text-white placeholder-gray-400 ${errors.content ? 'border-red-500/50 bg-red-500/10' : 'border-gray-600'
                  }`}
                placeholder="Write your post content here..."
              />
              {errors.content && (
                <p className="mt-1 text-sm text-red-400">{errors.content}</p>
              )}
              <p className="mt-1 text-sm text-gray-400">
                {content.length} characters
              </p>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full text-sm flex items-center border border-blue-500/30"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-2 text-blue-300 hover:text-blue-100"
                    >
                      <HiXMark className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                value={currentTag}
                onChange={handleTagInputChange}
                onKeyDown={handleTagKeyDown}
                className="w-full px-4 py-3 bg-dark-gray/50 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-white placeholder-gray-400"
                placeholder="Add tags (press Enter or comma to add)"
                disabled={tags.length >= 10}
              />
              {errors.tags && (
                <p className="mt-1 text-sm text-red-400">{errors.tags}</p>
              )}
              <p className="mt-1 text-sm text-gray-400">
                {tags.length}/10 tags. Press Enter or comma to add a tag.
              </p>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-700">
              <button
                type="button"
                onClick={() => navigate(`/post/${post.id}`)}
                className="px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700/50 transition-colors"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <span>Update Post</span>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default PostEdit;
